import { useIndexedDB } from "src/utils/useIndexedDB.js";
import { useEffect, useCallback } from "react";

export const useLessonStorage = () => {
  const initialLessons = [];

  const [savedLessons, setSavedLessons, , loading] = useIndexedDB(
    "maestro", // Same database as other app features
    "maestro-lessons", // New store for lesson data
    "lessons", // Key for the lessons array
    initialLessons
  );

  useEffect(() => {
    console.log("✅ savedLessons loaded:", savedLessons);
  }, [savedLessons]);

  // Extract lesson title from content (looks for **Lesson Title:** pattern)
  const extractTitleFromContent = (content) => {
    const titleMatch = content.match(/\*\*Lesson Title:\*\*\s*(.+?)(?:\n|$)/);
    return titleMatch
      ? titleMatch[1].trim()
      : `Lesson ${new Date().toLocaleDateString()}`;
  };

  // Extract unit day from content (looks for Day X patterns)
  const extractUnitDayFromContent = (content) => {
    const dayMatch = content.match(/Day\s+(\d+)/i);
    return dayMatch ? parseInt(dayMatch[1]) : null;
  };

  /**
   * Save a new lesson to IndexedDB
   * @param {Object} lessonData - The lesson data to save
   * @param {string} lessonData.content - The lesson plan content
   * @param {number} lessonData.unitDay - The unit day number
   * @param {string} [lessonData.title] - Optional custom title
   * @returns {string} The ID of the saved lesson
   */
  const saveLesson = (lessonData) => {
    const timestamp = Date.now();
    const extractedTitle = extractTitleFromContent(lessonData.content);
    const extractedUnitDay =
      extractUnitDayFromContent(lessonData.content) || lessonData.unitDay;

    const newLesson = {
      id: `lesson_${timestamp}`,
      title: lessonData.title || extractedTitle,
      unitDay: extractedUnitDay,
      content: lessonData.content,
      dateCreated: new Date().toISOString(),
      dateModified: new Date().toISOString(),
      version: 1,
    };

    setSavedLessons((prevLessons) => [...(prevLessons || []), newLesson]);
    return newLesson.id;
  };

  /**
   * Update an existing lesson
   * @param {string} lessonId - The ID of the lesson to update
   * @param {string} updatedContent - The new lesson content
   * @returns {boolean} Success status
   */
  const updateLesson = (lessonId, updatedContent) => {
    setSavedLessons((prevLessons) =>
      (prevLessons || []).map((lesson) =>
        lesson.id === lessonId
          ? {
              ...lesson,
              content: updatedContent,
              title: extractTitleFromContent(updatedContent),
              dateModified: new Date().toISOString(),
              version: (lesson.version || 1) + 1,
            }
          : lesson
      )
    );
    return true;
  };

  /**
   * Delete a lesson
   * @param {string} lessonId - The ID of the lesson to delete
   * @returns {boolean} Success status
   */
  const deleteLesson = (lessonId) => {
    setSavedLessons((prevLessons) =>
      (prevLessons || []).filter((lesson) => lesson.id !== lessonId)
    );
    return true;
  };

  /**
   * Get lessons for a specific unit day
   * @param {number} unitDay - The unit day to filter by
   * @returns {Array} Lessons for the specified unit day
   */
  const getLessonsByUnitDay = (unitDay) => {
    // Convert unitDay to number if it isn't already
    const dayNumber = typeof unitDay === "string" ? parseInt(unitDay) : unitDay;

    const filtered = (savedLessons || [])
      .filter((lesson) => {
        // Convert stored unitDay to number if needed
        const lessonDay =
          typeof lesson.unitDay === "string"
            ? parseInt(lesson.unitDay)
            : lesson.unitDay;
        return lessonDay === dayNumber;
      })
      .sort((a, b) => new Date(b.dateModified) - new Date(a.dateModified));

    return filtered;
  };

  /**
   * Get the most recent lesson for a unit day
   * @param {number} unitDay - The unit day
   * @returns {Object|null} The most recent lesson or null
   */
  const getLatestLessonForDay = (unitDay) => {
    const lessons = getLessonsByUnitDay(unitDay);
    return lessons.length > 0 ? lessons[0] : null;
  };

  /**
   * Get lesson by ID
   * @param {string} lessonId - The lesson ID
   * @returns {Object|null} The lesson or null
   */
  const getLessonById = (lessonId) => {
    return (
      (savedLessons || []).find((lesson) => lesson.id === lessonId) || null
    );
  };

  /**
   * Get all lessons sorted by date modified (most recent first)
   * @returns {Array} All lessons sorted by modification date
   */
  const getAllLessons = () => {
    return (savedLessons || []).sort(
      (a, b) => new Date(b.dateModified) - new Date(a.dateModified)
    );
  };

  return {
    savedLessons: savedLessons || [],
    loading,
    saveLesson,
    updateLesson,
    deleteLesson,
    getLessonsByUnitDay,
    getLatestLessonForDay,
    getLessonById,
    getAllLessons,
  };
};
